import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

/** Daily quota of AI pre-checks per Pro user (per UTC day). */
export const PREFLIGHT_DAILY_LIMIT = 30;
const KIND = "preflight";

function utcDay(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Read-only quota lookup (no increment). */
export const getPreflightQuota = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ used: number; limit: number; remaining: number; resetsAt: string }>=> {
    const day = utcDay();
    const { data } = await supabaseAdmin
      .from("pro_usage_daily")
      .select("count")
      .eq("user_id", context.userId)
      .eq("day", day)
      .eq("kind", KIND)
      .maybeSingle();
    const used = Math.max(0, Number(data?.count ?? 0));
    const limit = PREFLIGHT_DAILY_LIMIT;
    // Next UTC midnight ISO
    const next = new Date();
    next.setUTCHours(24, 0, 0, 0);
    return { used, limit, remaining: Math.max(0, limit - used), resetsAt: next.toISOString() };
  });

/**
 * Preflight capture coach (Phase 3 — L3).
 *
 * Lightweight pre-shot check: receives a downsized preview frame and returns
 * actionable shooting hints (move closer, more light, hold steady, etc.) plus
 * a boolean `ready` so the UI can green-light a real capture or a guided burst.
 *
 * Non-diagnostic by design: no condition naming, no medical advice — strictly
 * photographic guidance for reproducible follow-ups.
 */

const BODY_AREAS = [
  "face","scalp","eyebrows_lashes","neck_decollete","scar_site",
  "hands","abdomen","limb","full_body","custom",
] as const;

const Input = z.object({
  imageBase64: z.string().min(100).max(8_000_000),
  bodyArea: z.enum(BODY_AREAS),
  locale: z.enum(["fr","en","nl"]).default("fr"),
});

export type PreflightResult = {
  ready: boolean;
  score: number;            // 0..100 overall shootability
  hints: string[];          // up to 3 short imperative hints in user locale
  reason?: string | null;   // short rationale when not ready
  quota?: { used: number; limit: number; remaining: number; resetsAt: string };
};

const localeName = { fr: "français", en: "English", nl: "Nederlands" } as const;

export const preflightCapture = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => Input.parse(input))
  .handler(async ({ data, context }): Promise<PreflightResult> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("Missing LOVABLE_API_KEY");

    // ── Quota check (per-user, per UTC day) ───────────────────────────────
    const day = utcDay();
    const limit = PREFLIGHT_DAILY_LIMIT;
    const { data: row } = await supabaseAdmin
      .from("pro_usage_daily")
      .select("count")
      .eq("user_id", context.userId)
      .eq("day", day)
      .eq("kind", KIND)
      .maybeSingle();
    const used = Math.max(0, Number(row?.count ?? 0));
    if (used >= limit) {
      // Throw a tagged error the UI parses to render the proper 429 message.
      throw new Error(`quota_exceeded:${limit}:${day}`);
    }

    const sys = [
      `You are a photo-coach for a wellness tracking app.`,
      `Reply in ${localeName[data.locale]}.`,
      `Goal: tell the user — in 1 to 3 short imperative hints — how to improve the framing of a follow-up photo of the "${data.bodyArea}" area.`,
      `STRICT RULES:`,
      `- Photographic guidance ONLY (light, distance, angle, steadiness, background, scale ref).`,
      `- No condition names, no medical advice, no diagnosis, no prognosis.`,
      `- Hints must be <= 8 words each.`,
      `Return ONLY valid JSON: {"ready": boolean, "score": 0..100, "hints": ["..."], "reason": "string or null"}.`,
      `ready=true ONLY when score >= 75 and no critical issue remains.`,
    ].join("\n");

    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash-lite",
        messages: [
          { role: "system", content: sys },
          { role: "user", content: [
            { type: "text", text: `Body area: ${data.bodyArea}. Evaluate framing & shoot conditions. JSON only.` },
            { type: "image_url", image_url: { url: data.imageBase64 } },
          ] },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (res.status === 429) throw new Error("rate_limited");
    if (res.status === 402) throw new Error("credits_exhausted");
    if (!res.ok) throw new Error(`ai_error_${res.status}`);
    const json = await res.json();
    let parsed: any = {};
    try { parsed = JSON.parse(json.choices?.[0]?.message?.content ?? "{}"); } catch { parsed = {}; }

    const hintsRaw = Array.isArray(parsed.hints) ? parsed.hints.slice(0, 3) : [];
    const hints = hintsRaw
      .filter((h: unknown): h is string => typeof h === "string")
      .map((h: string) => h.trim().slice(0, 80))
      .filter(Boolean);

    const score = Math.max(0, Math.min(100, Number(parsed.score) || 0));

    // ── Increment counter only after a successful upstream call ──────────
    const newCount = used + 1;
    await supabaseAdmin
      .from("pro_usage_daily")
      .upsert(
        { user_id: context.userId, day, kind: KIND, count: newCount, updated_at: new Date().toISOString() },
        { onConflict: "user_id,day,kind" },
      );
    const next = new Date();
    next.setUTCHours(24, 0, 0, 0);

    return {
      ready: Boolean(parsed.ready) && score >= 75,
      score,
      hints,
      reason: typeof parsed.reason === "string" ? parsed.reason.slice(0, 200) : null,
      quota: { used: newCount, limit, remaining: Math.max(0, limit - newCount), resetsAt: next.toISOString() },
    };
  });